<template>
  <div class="offline-indicator-container">
    <!-- Authentication Warning (when offline and not authenticated) -->
    <transition name="slide-down">
      <div
        v-if="!isOnline && !isAuthenticated"
        class="offline-indicator status-warning"
      >
        <div class="flex items-center gap-2">
          <i class="fas fa-exclamation-triangle text-lg"></i>
          <span class="font-semibold">Login Required for Offline Mode</span>
        </div>
        <div class="text-xs mt-1 opacity-90">
          You must be logged in to record transactions offline. Please connect to the internet and log in.
        </div>
      </div>
    </transition>

    <!-- Network Status Badge -->
    <transition name="slide-down">
      <div
        v-if="(!isOnline || pendingCount > 0 || failedCount > 0) && isAuthenticated"
        class="offline-indicator"
        :class="statusClass"
      >
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-2">
            <!-- Status Icon -->
            <i
              :class="statusIcon"
              class="text-lg"
            ></i>
            
            <!-- Status Text -->
            <span class="font-semibold">{{ statusText }}</span>
          </div>

          <!-- Pending Count Badge -->
          <div v-if="pendingCount > 0 || failedCount > 0" class="flex items-center gap-2">
            <span class="text-sm" v-if="pendingCount > 0">
              {{ pendingCount }} pending
            </span>
            <span class="text-sm text-red-600 font-bold ml-2" v-if="failedCount > 0">
              {{ failedCount }} failed
            </span>
            
            <!-- Sync Button -->
            <button
              v-if="isOnline && !isSyncing"
              @click="handleSync"
              class="sync-button"
              title="Sync now"
            >
              <i class="fas fa-sync-alt"></i>
            </button>

            <!-- Syncing Spinner -->
            <div v-if="isSyncing" class="syncing-spinner">
              <i class="fas fa-spinner fa-spin"></i>
            </div>
          </div>
        </div>

        <!-- Last Sync Time -->
        <div v-if="lastSyncTime && isOnline" class="text-xs mt-1 opacity-75">
          Last synced: {{ formatLastSync(lastSyncTime) }}
        </div>

        <!-- Sync Errors -->
        <div v-if="syncErrors.length > 0" class="mt-2">
          <div class="text-xs font-semibold mb-1">Sync Errors:</div>
          <div
            v-for="(error, index) in syncErrors.slice(0, 3)"
            :key="index"
            class="text-xs opacity-90 truncate"
          >
            • {{ error }}
          </div>
          <button
            v-if="failedCount > 0"
            @click="handleRetry"
            class="retry-button mt-2"
          >
            <i class="fas fa-redo mr-1"></i>
            Retry Failed
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useOfflineSync } from '@/composables/useOfflineSync'
import { offlineStorageService } from '@/services/offlineStorageService'

const {
  isOnline,
  networkStatus,
  isSyncing,
  pendingCount,
  failedCount,
  lastSyncTime,
  syncErrors,
  manualSync,
  retryFailed
} = useOfflineSync()

// Check if user is authenticated for offline operations
const isAuthenticated = computed(() => {
  return offlineStorageService.getAuthenticatedUser() !== null
})

const statusClass = computed(() => {
  if (!isOnline.value) return 'status-offline'
  if (isSyncing.value) return 'status-syncing'
  if (pendingCount.value > 0) return 'status-pending'
  if (failedCount.value > 0) return 'status-warning'
  return 'status-online'
})

const statusIcon = computed(() => {
  if (!isOnline.value) return 'fas fa-wifi-slash'
  if (isSyncing.value) return 'fas fa-sync-alt fa-spin'
  if (pendingCount.value > 0) return 'fas fa-cloud-upload-alt'
  if (failedCount.value > 0) return 'fas fa-exclamation-circle text-red-600'
  return 'fas fa-cloud-upload-alt'
})

const statusText = computed(() => {
  if (!isOnline.value) return 'Offline Mode'
  if (isSyncing.value) return 'Syncing...'
  if (pendingCount.value > 0) return 'Pending Sync'
  if (failedCount.value > 0) return 'Sync Failed'
  return 'Synced'
})

const handleSync = async () => {
  try {
    await manualSync()
  } catch (error) {
    console.error('Manual sync failed:', error)
  }
}

const handleRetry = async () => {
  try {
    await retryFailed()
  } catch (error) {
    console.error('Retry failed:', error)
  }
}

const formatLastSync = (timestamp: number): string => {
  const now = Date.now()
  const diff = now - timestamp
  const minutes = Math.floor(diff / 60000)
  
  if (minutes < 1) return 'just now'
  if (minutes === 1) return '1 minute ago'
  if (minutes < 60) return `${minutes} minutes ago`
  
  const hours = Math.floor(minutes / 60)
  if (hours === 1) return '1 hour ago'
  if (hours < 24) return `${hours} hours ago`
  
  return new Date(timestamp).toLocaleString()
}
</script>

<style scoped>
.offline-indicator-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  pointer-events: none;
}

.offline-indicator {
  pointer-events: auto;
  padding: 0.75rem 1rem;
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.status-offline {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.status-syncing {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.status-pending {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.status-online {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.status-warning {
  background: linear-gradient(135deg, #eab308 0%, #ca8a04 100%);
}

.sync-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.sync-button:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.sync-button:active {
  transform: scale(0.95);
}

.syncing-spinner {
  color: white;
  font-size: 1rem;
}

.retry-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.75rem;
  font-weight: 600;
}

.retry-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Slide down animation */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
